'use client';

import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type TrailDetails from '@alltrails/core/types/TrailDetails';
import Carousel from '@alltrails/denali/components/Carousel';
import { TrailPage } from '@alltrails/trails';
import styles from './HitTheTrail.module.scss';
import HitTheTrailShimmerCard from './HitTheTrailShimmerCard';
import SectionHeader from '@/components/SectionHeader';

const OpenInApp = dynamic(() => import('./OpenInApp/OpenInApp'), { loading: HitTheTrailShimmerCard, ssr: false });
const DownloadOnDevice = dynamic(() => import('./DownloadOnDevice/DownloadOnDevice'), { loading: HitTheTrailShimmerCard, ssr: false });
const LiveShare = dynamic(() => import('./LiveShare/LiveShare'), { loading: HitTheTrailShimmerCard, ssr: false });

// https://www.figma.com/design/oevB4bXh8KLLDe1Yp8rBW2/Trail-Page-Web-[Handoff]?node-id=14462-236943

type HitTheTrailProps = {
  isBot: boolean;
  trailDetails: TrailDetails;
  trailPage: TrailPage;
};

const HitTheTrail = ({ isBot, trailDetails, trailPage }: HitTheTrailProps) => {
  const intl = useIntl();
  const [showArrows, setShowArrows] = useState(true);
  const [separateLinePlusBadges, setSeparateLinePlusBadges] = useState(false);
  const isMobile = useIsMobileSizedScreen();

  const sharedCardProps = {
    showArrow: showArrows,
    setShowAllCardArrows: setShowArrows,
    separateLinePlusBadge: separateLinePlusBadges,
    setSeparateLinePlusBadges,
    trailDetails,
    trailPage
  };

  const cardsOrder = isMobile ? ['download', 'open-in-app', 'share-live'] : ['open-in-app', 'download', 'share-live'];

  const cards = {
    'open-in-app': <OpenInApp key="open-in-app" {...sharedCardProps} />,
    download: <DownloadOnDevice key="download-on-device" {...sharedCardProps} />,
    'share-live': <LiveShare key="share-live" {...sharedCardProps} />
  };

  return (
    <section aria-label={intl.formatMessage({ defaultMessage: 'Hit the trail' })}>
      <SectionHeader title={<FormattedMessage defaultMessage="Hit the trail" />} />
      {isBot ? (
        <div className={styles.botContainer}>{Object.keys(cards).map(cardKey => cards[cardKey])}</div>
      ) : (
        <Carousel
          ariaLabel={intl.formatMessage({ defaultMessage: 'Hit the trail carousel' })}
          uniqueId="hit-the-trail"
          items={cardsOrder}
          renderItem={cardKey => cards[cardKey]}
          getItemKey={cardKey => cardKey}
          slidesPerViewMobile={1}
          slidesPerViewBreakpoints={{ 361: 1, 541: 2, 1121: 3 }}
          showPaginationDots={false}
          className={classNames(styles.carousel, { [styles.mobileCarousel]: isMobile })}
        />
      )}
    </section>
  );
};

export default HitTheTrail;
